import { CategoryData } from './residentialCategories'
import { residentialCategories } from './residentialCategories'
import { commercialCategories } from './commercialCategories'
import { signsAccentsCategories } from './signsAccentsCategories'
import { flattenNavRoutes, NavGroupId } from './categoryNav'

export type CategoryContext =
  | 'Mailboxes'
  | 'Commercial'
  | 'Street Signs & Traffic Signs'
  | 'Decorative Signage'
  | 'Outdoor Accessories & Home Accents'
  | 'Outdoor Lighting'

export type CategoryRoute = {
  route: string
  label: string
  context: CategoryContext
  /**
   * Optional route whose sample product data should be reused for this entry.
   * Used when a category is renamed/re-homed but the underlying catalog data
   * still lives on the legacy route.
   */
  dataRoute?: string
  /**
   * Copy shown under the H1 for categories that have no external SEO entry yet
   * (typically newly proposed categories in the restructured tree).
   */
  description?: string
  /**
   * Overrides the externally loaded SEO entry for renamed categories, so the
   * browser title, meta description, and intro copy match the restructured
   * catalog instead of the legacy category name.
   */
  seo?: {
    pageTitle?: string
    metaDescription?: string
    description?: string
  }
}

const OUTDOOR_ACCESSORIES_SEO = {
  pageTitle:
    'Outdoor Accessories & Home Accents | Planters, Address Plaques & Exterior Decor',
  metaDescription:
    'Shop outdoor accessories and home accents from MidAtlantic Mailbox: address plaques and house numbers, illuminated address plaques, planters and window boxes, country bells, patio coolers, door chimes and doorbells, garden accents, and exterior home accents.',
  description:
    'Address plaques and house numbers, illuminated address plaques, planters and window boxes, country bells, patio coolers, door chimes and doorbells, plus garden and exterior home accents that coordinate with your mailbox and post finishes.',
}

/**
 * Authoritative list of every visible category/listing route, organized by the
 * proposed customer-facing catalog structure. These are recognized as category
 * pages BEFORE the generic legacy fallback, even when there is no matching
 * sample product data yet.
 */
const BASE_CATEGORY_ROUTES: CategoryRoute[] = [
  // ---------------------------------------------------------------- Mailboxes
  { route: '/6m8/mailboxes.html', label: 'Mailboxes', context: 'Mailboxes' },
  {
    route: '/6m8/curbside-mailboxes.html',
    label: 'Curbside Mailboxes',
    context: 'Mailboxes',
  },
  {
    route: '/6m8/mailboxes/complete-mailbox-post-system.html',
    label: 'Complete Mailbox & Post Systems',
    context: 'Mailboxes',
  },
  {
    route: '/6m8/mailboxes/multi-mount-mailbox-systems.html',
    label: 'Multi Mount Mailbox Systems',
    context: 'Mailboxes',
    description:
      'Dual and multi-unit mailbox systems that mount two or more mailboxes on a single post or frame — a top-level mailbox category of its own, separate from single complete mailbox and post packages.',
  },
  {
    route: '/6m8/mailboxes/wall-mount-mailboxes.html',
    label: 'Wall Mount Mailboxes',
    context: 'Mailboxes',
  },
  {
    route: '/6m8/mailbox-posts.html',
    label: 'Mailbox Posts',
    context: 'Mailboxes',
  },
  {
    route: '/6m8/mailboxes/locking-mailboxes.html',
    label: 'Locking Mailboxes',
    context: 'Mailboxes',
  },
  {
    route: '/6m8/mailboxes/stone-brick-column-mailboxes.html',
    label: 'Stone & Brick Column Mailboxes',
    context: 'Mailboxes',
  },
  {
    route: '/6m8/mailboxes/mailbox-replacement-parts-and-accessories.html',
    label: 'Mailbox Parts & Accessories',
    context: 'Mailboxes',
  },
  {
    route: '/6m8/mailboxes/replacement-numbers.html',
    label: 'Replacement Numbers',
    context: 'Mailboxes',
  },

  // --------------------------------- Commercial Mailboxes & Parcel Lockers
  {
    route: '/6m8/commercial-mailboxes-lockers.html',
    label: 'Commercial Mailboxes & Parcel Lockers',
    context: 'Commercial',
  },
  {
    route:
      '/6m8/commercial-mailboxes-lockers/centralized-delivery-mailboxes-and-kiosks.html',
    label: 'Cluster Mailboxes, CBUs & 4C Mailboxes',
    context: 'Commercial',
  },
  {
    route: '/6m8/commercial-mailboxes-lockers/parcel-lockers.html',
    label: 'Parcel Lockers',
    context: 'Commercial',
  },
  {
    route: '/6m8/commercial-mailboxes-lockers/collection-boxes.html',
    label: 'Collection Boxes',
    context: 'Commercial',
  },
  {
    route: '/6m8/commercial-mailboxes-lockers/key-keepers-key-blanks.html',
    label: 'Key Keepers & Key Blanks',
    context: 'Commercial',
  },
  {
    route:
      '/6m8/commercial-mailboxes-lockers/mail-slots-letter-book-drops.html',
    label: 'Mail Slots, Letter & Book Drops',
    context: 'Commercial',
  },
  {
    route:
      '/6m8/commercial-mailboxes-lockers/replacement-commercial-locks-hinges-doors.html',
    label: 'Commercial Replacement Parts',
    context: 'Commercial',
  },
  {
    route: '/6m8/commercial-mailboxes-lockers/private-delivery-mailboxes.html',
    label: 'Private Delivery Mailboxes',
    context: 'Commercial',
  },

  // ------------------------------------------- Street Signs & Traffic Signs
  {
    route: '/6m8/street-signs-traffic-signs.html',
    label: 'Street Signs & Traffic Signs',
    context: 'Street Signs & Traffic Signs',
  },
  {
    route: '/6m8/street-signs-traffic-signs/traffic-road-signs.html',
    label: 'Traffic Signs',
    context: 'Street Signs & Traffic Signs',
  },
  {
    route: '/6m8/street-signs-traffic-signs/sign-frames-back-plates.html',
    label: 'Sign Frames & Back Plates',
    context: 'Street Signs & Traffic Signs',
  },

  // ------------------------------------------------------- Decorative Signage
  {
    route: '/6m8/decorative-signage.html',
    label: 'Decorative Signage',
    context: 'Decorative Signage',
  },
  {
    route: '/6m8/decorative-signage/replacement-bases-finials-sign-mounts.html',
    label: 'Sign Frames, Bases & Hardware',
    context: 'Decorative Signage',
  },
  {
    route: '/6m8/message-boards.html',
    label: 'Message Boards',
    context: 'Decorative Signage',
  },
  {
    route: '/6m8/decorative-signage/decorative-street-sign.html',
    label: 'Decorative Street Signs',
    context: 'Decorative Signage',
  },
  {
    route: '/6m8/decorative-signage/decorative-stop-signs.html',
    label: 'Decorative Stop Signs',
    context: 'Decorative Signage',
  },
  {
    route: '/6m8/decorative-signage/decorative-traffic-signage.html',
    label: 'Decorative Traffic Signage',
    context: 'Decorative Signage',
  },
  {
    route: '/6m8/decorative-signage/sign-frames-back-plates.html',
    label: 'Sign Frames & Back Plates',
    context: 'Decorative Signage',
  },

  // ------------------------------- Outdoor Accessories & Home Accents
  {
    route: '/6m8/outdoor-accessories-home-accents.html',
    label: 'Outdoor Accessories & Home Accents',
    context: 'Outdoor Accessories & Home Accents',
    dataRoute: '/6m8/home-garden-accents.html',
    seo: OUTDOOR_ACCESSORIES_SEO,
  },
  {
    route: '/6m8/address-plaques-and-house-numbers.html',
    label: 'Address Plaques & House Numbers',
    context: 'Outdoor Accessories & Home Accents',
  },
  {
    route:
      '/6m8/address-plaques-and-house-numbers/illuminated-address-plaques.html',
    label: 'Illuminated Address Plaques',
    context: 'Outdoor Accessories & Home Accents',
    description:
      'Lighted and solar address plaques that keep your house numbers visible after dark, grouped with the rest of our address plaques and house numbers.',
    dataRoute: '/6m8/address-plaques-and-house-numbers.html',
  },
  {
    route: '/6m8/outdoor-accessories-home-accents/planters-window-boxes.html',
    label: 'Planters & Window Boxes',
    context: 'Outdoor Accessories & Home Accents',
    description:
      'Patio planters, tall planters, and window box planters that coordinate with your mailbox, post, and exterior finishes.',
    dataRoute: '/6m8/home-garden-accents.html',
  },
  {
    route: '/6m8/outdoor-accessories-home-accents/home-exterior-accents.html',
    label: 'Home Exterior Accents',
    context: 'Outdoor Accessories & Home Accents',
    description:
      'Finishing details for the exterior of your home, from hose holders and wall accents to decorative hardware.',
  },
  {
    route: '/6m8/outdoor-accessories-home-accents/garden-decor.html',
    label: 'Garden Decor',
    context: 'Outdoor Accessories & Home Accents',
    description:
      'Decorative pieces for the yard and garden that match the cast-aluminum finishes used across our mailbox and post lines.',
  },
  {
    route: '/6m8/outdoor-accessories-home-accents/country-bells.html',
    label: 'Country Bells',
    context: 'Outdoor Accessories & Home Accents',
    description:
      'Cast-aluminum country bells in multiple sizes and finishes for porches, patios, and garden entries.',
  },
  {
    route: '/6m8/outdoor-accessories-home-accents/patio-coolers.html',
    label: 'Patio Coolers',
    context: 'Outdoor Accessories & Home Accents',
    description:
      'Weather-resistant patio coolers built to live outdoors alongside the rest of your outdoor accessories.',
  },
  {
    route: '/6m8/mailboxes/door-chimes-doorbells.html',
    label: 'Door Chimes & Doorbells',
    context: 'Outdoor Accessories & Home Accents',
  },
  // Legacy home & garden paths kept renderable under the new group name.
  {
    route: '/6m8/home-garden-accents.html',
    label: 'Outdoor Accessories & Home Accents',
    context: 'Outdoor Accessories & Home Accents',
    seo: OUTDOOR_ACCESSORIES_SEO,
  },
  {
    route: '/6m8/home-garden-accents/home-accents.html',
    label: 'Home Exterior Accents',
    context: 'Outdoor Accessories & Home Accents',
    seo: {
      pageTitle:
        'Home Exterior Accents | Country Bells, Hose Holders & Exterior Decor',
      metaDescription:
        'Exterior home accents from MidAtlantic Mailbox, including country bells, hose holders, and decorative cast-aluminum accents that coordinate with your mailbox and post finishes.',
      description:
        'Country bells, hose holders, and decorative cast-aluminum accents that finish the exterior of your home and coordinate with your mailbox and post finishes.',
    },
  },
  {
    route: '/6m8/home-garden-accents/planters.html',
    label: 'Planters & Window Boxes',
    context: 'Outdoor Accessories & Home Accents',
    seo: {
      pageTitle: 'Planters & Window Boxes | Patio Planters & Window Box Planters',
      metaDescription:
        'Patio planters, tall planters, and window box planters from MidAtlantic Mailbox that coordinate with your mailbox, post, and exterior finishes.',
      description:
        'Patio planters, tall planters, and window box planters that coordinate with your mailbox, post, and exterior finishes.',
    },
  },
  {
    route: '/6m8/home-garden-accents/window-box-planters.html',
    label: 'Window Box Planters',
    context: 'Outdoor Accessories & Home Accents',
  },
  {
    route: '/6m8/home-garden-accents/fairfield-50qt-coolers.html',
    label: 'Patio Coolers',
    context: 'Outdoor Accessories & Home Accents',
  },

  // ------------------------------------------------------- Outdoor Lighting
  {
    route: '/6m8/lighting.html',
    label: 'Outdoor Lighting',
    context: 'Outdoor Lighting',
  },
  {
    route: '/6m8/lighting/outdoor-lamp-posts.html',
    label: 'Outdoor Lamp Posts',
    context: 'Outdoor Lighting',
  },
  {
    // Legacy route preserved; label follows the category report.
    route: '/6m8/lighting/post-lights-fixtures.html',
    label: 'Exterior Home Lighting',
    context: 'Outdoor Lighting',
    description:
      'Exterior home lighting for entries, walkways, and facades, including post-mount lanterns, globes, and fixtures that pair with our outdoor lamp posts.',
    dataRoute: '/6m8/lighting.html',
  },
  {
    route: '/6m8/lighting/commercial-lighting.html',
    label: 'Commercial Lighting',
    context: 'Outdoor Lighting',
  },
  {
    route: '/6m8/lighting/light-blocks.html',
    label: 'Light Blocks',
    context: 'Outdoor Lighting',
  },
  {
    route: '/6m8/lighting/outdoor-exterior-lighting.html',
    label: 'Outdoor Exterior Lighting',
    context: 'Outdoor Lighting',
  },
  {
    route: '/6m8/lighting/exterior-home-lighting.html',
    label: 'Exterior Home Lighting',
    context: 'Outdoor Lighting',
    description:
      'Exterior home lighting for entries, walkways, and facades, including post-mount lanterns, globes, and fixtures that pair with our outdoor lamp posts.',
    dataRoute: '/6m8/lighting.html',
  },
]

const CONTEXT_BY_GROUP: Record<NavGroupId, CategoryContext> = {
  mailboxes: 'Mailboxes',
  commercial: 'Commercial',
  'street-signs': 'Street Signs & Traffic Signs',
  'decorative-signage': 'Decorative Signage',
  'outdoor-accessories': 'Outdoor Accessories & Home Accents',
  lighting: 'Outdoor Lighting',
}

/**
 * Every route in the shared nav tree becomes a real category page. Explicit
 * entries above win; nested subcategories introduced by the tree inherit their
 * closest ancestor's product data so links never fall through to the generic
 * legacy fallback.
 */
const baseByRoute = new Map(BASE_CATEGORY_ROUTES.map((e) => [e.route, e]))
const derivedFromNav: CategoryRoute[] = []
for (const node of flattenNavRoutes()) {
  if (baseByRoute.has(node.route)) continue
  const parent = baseByRoute.get(node.parentRoute)
  derivedFromNav.push({
    route: node.route,
    label: node.label,
    context: CONTEXT_BY_GROUP[node.groupId],
    dataRoute: parent?.dataRoute || node.parentRoute,
    description: `Browse ${node.label} within ${
      parent?.label || CONTEXT_BY_GROUP[node.groupId]
    }. Call 301-620-7585 if you need help matching a finish, size, or mounting style.`,
  })
  baseByRoute.set(node.route, derivedFromNav[derivedFromNav.length - 1])
}

export const CATEGORY_SEO_PATHS: CategoryRoute[] = [
  ...BASE_CATEGORY_ROUTES,
  ...derivedFromNav,
]

const PRODUCT_DATA_BY_ROUTE: Record<string, CategoryData> = {}
for (const c of [
  ...residentialCategories,
  ...commercialCategories,
  ...signsAccentsCategories,
]) {
  PRODUCT_DATA_BY_ROUTE[c.route] = c
}

/**
 * Resolve a category route to renderable CategoryData. If the route (or its
 * mapped dataRoute) has sample product data, those products are returned with
 * the registry's current label so renamed categories read correctly. Otherwise
 * a polished empty category is returned so the page still renders with the SEO
 * description and a product-help state instead of the legacy placeholder.
 */
export function getCategoryForRoute(entry: CategoryRoute): CategoryData {
  const existing =
    PRODUCT_DATA_BY_ROUTE[entry.route] ||
    (entry.dataRoute ? PRODUCT_DATA_BY_ROUTE[entry.dataRoute] : undefined)
  if (existing) {
    // Keep the data route so external SEO copy still resolves, but present the
    // category under its current (restructured) label.
    return {
      ...existing,
      label: entry.label,
    }
  }
  return {
    route: entry.route,
    label: entry.label,
    sourceUrl: `https://www.midatlanticmailboxes.com${entry.route}`,
    total: 0,
    products: [],
  }
}
